import type { Ticket, Profile, Session } from "./types";
export const safeDate = (value: unknown): Date | null => {
  if (value == null || value === "") return null;
  const d = new Date(value as string);
  return Number.isFinite(d.getTime()) ? d : null;
};
export const digits = (s: string) => s.replace(/\D/g, "");
export function validCpf(value: string) {
  const s = digits(value);
  if (!value) return true;
  if (s.length !== 11 || /^(\d)\1{10}$/.test(s)) return false;
  return [9, 10].every((n) => {
    let sum = 0;
    for (let i = 0; i < n; i++) sum += Number(s[i]) * (n + 1 - i);
    return Number(s[n]) === (sum % 11 < 2 ? 0 : 11 - (sum % 11));
  });
}
export function validBirthDate(s: string, now = new Date()) {
  if (!s) return true;
  const d = new Date(`${s}T12:00:00`);
  return (
    Number.isFinite(d.getTime()) &&
    d.getFullYear() > 1899 &&
    s <=
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}` &&
    d.getFullYear() === Number(s.slice(0, 4)) &&
    d.getMonth() + 1 === Number(s.slice(5, 7)) &&
    d.getDate() === Number(s.slice(8, 10))
  );
}
export function splitName(s: string) {
  const [firstName, ...rest] = s.trim().split(/\s+/);
  return { firstName, lastName: rest.join(" ") };
}
export function feedbackAnswered(ticket: Ticket) {
  return ticket.feedback?.lastUpdate != null || ticket.feedback?.rate != null;
}
export function expired(ticket: Ticket, now = Date.now()) {
  const end = safeDate(ticket.session?.end);
  return (
    !["completed", "canceled"].includes(ticket.status) &&
    end !== null &&
    end.getTime() < now
  );
}
export function shouldRestore(
  ticket: Ticket,
  lastFeedback: number | null,
  now = Date.now(),
) {
  return (
    !["completed", "canceled"].includes(ticket.status) &&
    !feedbackAnswered(ticket) &&
    ticket.id !== lastFeedback &&
    !expired(ticket, now)
  );
}
export type Step =
  | "scheduling"
  | "shopping"
  | "timer"
  | "checkout"
  | "checked"
  | "called"
  | "started"
  | "completed";
export function ticketStep(ticket: Ticket | null, now = Date.now()): Step {
  if (!ticket || ticket.status === "canceled") return "scheduling";
  if (["checked", "called", "started", "completed"].includes(ticket.status))
    return ticket.status as Step;
  if (ticket.status === "confirmed") {
    const date = safeDate(ticket.prevision);
    return date && date.getTime() - 180000 > now ? "timer" : "checkout";
  }
  return "shopping";
}
export function sessionAvailable(s: Session) {
  return s.hasSlotsLeft;
}
export function parseScan(
  value: string,
): { kind: "terminal" | "pit"; value: string } | null {
  const v = value.trim();
  if (/^\d{6}$/.test(v)) return { kind: "pit", value: v };
  try {
    const u = new URL(v);
    if (!["https:", "http:"].includes(u.protocol)) return null;
    const match = u.pathname.match(/^\/in\/([A-Za-z0-9_-]{1,100})\/?$/);
    if (match) return { kind: "terminal", value: match[1] };
  } catch {
    /* Bare terminal access key. */
  }
  return /^[A-Za-z0-9_-]{1,100}$/.test(v)
    ? { kind: "terminal", value: v }
    : null;
}
export function mapTicket(
  raw: Record<string, unknown>,
  current?: Ticket,
): Ticket {
  const d = raw as Partial<Ticket> & {
    location?: Record<string, unknown>;
    estimatedServiceStart?: string;
    activities?: Ticket["history"];
    session?: Session & { estimatedStart?: string; estimatedEnd?: string };
  };
  const loc = d.location;
  const {
    location: _location,
    estimatedServiceStart: _estimate,
    activities: _activities,
    ...clean
  } = d;
  const result = {
    ...current,
    ...clean,
    id: d.id ?? current?.id ?? 0,
    provider: d.provider ?? current?.provider,
    resource: d.resource ?? current?.resource,
    feedback: d.feedback ?? current?.feedback,
    businessUnit: loc?.id
      ? {
          id: Number(loc.id),
          name: String(loc.name || ""),
          address: String(loc.address || ""),
          city: String(loc.city || ""),
          state: String(loc.state || ""),
          zipCode: String(loc.zipcode || ""),
          providerId: Number(loc.providerId),
          timeZone: String(loc.timezoneId || "America/Sao_Paulo"),
        }
      : (d.businessUnit ?? current?.businessUnit),
    session: d.session?.id
      ? {
          ...d.session,
          start: safeDate(d.session.estimatedStart ?? d.session.start),
          end: safeDate(d.session.estimatedEnd ?? d.session.end),
        }
      : (current?.session ?? {
          id: 0,
          start: null,
          end: null,
          hasSlotsLeft: false,
          resourceId: 0,
          attendanceServiceType: 0,
          isOverTime: false,
        }),
    prevision: safeDate(
      Object.hasOwn(d, "estimatedServiceStart")
        ? d.estimatedServiceStart
        : Object.hasOwn(d, "prevision")
          ? d.prevision
          : current?.prevision,
    ),
    status: d.status?.toLowerCase() ?? current?.status ?? "unknown",
    history: d.activities ?? d.history ?? current?.history ?? [],
    actions: d.actions ?? current?.actions ?? [],
    callCount: d.callCount ?? current?.callCount ?? 0,
    position: d.position ?? current?.position ?? "--",
  } as Ticket;
  const session = result.session as Session & {
    estimatedStart?: unknown;
    estimatedEnd?: unknown;
  };
  delete session.estimatedStart;
  delete session.estimatedEnd;
  return result;
}
export function birthDate(profile: Profile | null) {
  return profile?.birthDateYear &&
    profile.birthDateMonth &&
    profile.birthDateDay
    ? `${profile.birthDateYear}-${profile.birthDateMonth.padStart(2, "0")}-${profile.birthDateDay.padStart(2, "0")}`
    : "";
}
